import {
  useGetAttachmentById,
  type Attachment,
} from "@/api/attachment";
import { type MediaSize } from "@/components/cms-puck/fields/MediaPickerField";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import { catalogMediaKindBadge } from "@/lib/cms-puck/media-utils";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Aperçu éditeur d'un média sélectionné (fetch de l'Attachment par id).
 * Image → vignette selon `size`. Vidéo / embed → lecteur (parité front
 * `CmsCoverMedia` / `VideoPlayer`) pour pouvoir prévisualiser la lecture dans
 * le canvas Puck. Si l'id est vide/introuvable, placeholder neutre.
 */
export function MediaPreview({
  attachmentId,
  size,
  className,
  style,
  sampleUrl,
}: {
  attachmentId: string | undefined;
  /** Taille d'affichage (image). Défaut "large" (parité front + rétro-compat). */
  size?: MediaSize;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Échappatoire URL directe réservée à l'APERÇU de la bibliothèque de modèles :
   * une image d'exemple statique (servie depuis `public/cms-samples/`) à afficher
   * SANS passer par la résolution d'Attachment par id (qui nécessiterait un média
   * réel de l'instance). Jamais écrite dans une page : injectée uniquement dans le
   * layout de prévisualisation (cf. modeles.tsx). Prioritaire si fournie.
   */
  sampleUrl?: string;
}) {
  const { data } = useGetAttachmentById(attachmentId ?? "", {
    pollWhileProcessing: true,
  });
  if (sampleUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={sampleUrl} alt="" className={className} style={style} />;
  }
  const att = attachmentId ? (data as Attachment | undefined) : undefined;

  if (att?.kind === "video") {
    if (att.videoPublicUrl) {
      return (
        <ResponsiveVideo
          className={cn("w-full max-w-full", className)}
          src={att.videoPublicUrl}
          poster={att.posterPublicUrl ?? undefined}
        />
      );
    }
    if (att.videoStatus === "processing") {
      return (
        <div
          className={cn(
            "flex min-h-40 flex-col items-center justify-center gap-2 rounded border border-dashed border-slate-300 text-sm text-slate-500",
            className,
          )}
          style={style}
        >
          <Loader2 className="h-8 w-8 animate-spin" />
          <span>
            {cmsNew(
              "cms.new.preview.media.videoProcessing",
              "Conversion vidéo en cours…",
            )}
          </span>
        </div>
      );
    }
    if (att.videoStatus === "error") {
      return (
        <div
          className={cn(
            "flex min-h-40 items-center justify-center rounded border border-dashed border-red-300 text-sm text-red-600",
            className,
          )}
          style={style}
        >
          {cmsNew(
            "cms.new.preview.media.videoError",
            "Erreur de conversion vidéo",
          )}
        </div>
      );
    }
    const poster =
      att.posterPublicUrl ?? att.mediumPublicUrl ?? att.publicUrl ?? undefined;
    if (poster) {
      return (
        <div className="relative inline-block h-full w-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={poster}
            alt={att.name ?? ""}
            className={className}
            style={style}
          />
          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {catalogMediaKindBadge("video")}
          </span>
        </div>
      );
    }
    return (
      <div
        className={cn(
          "flex min-h-40 items-center justify-center rounded border border-dashed border-slate-300 text-sm text-slate-400",
          className,
        )}
        style={style}
      >
        {cmsNew("cms.new.preview.media.videoPending", "Vidéo sélectionnée")}
      </div>
    );
  }

  if (att?.kind === "embed") {
    return (
      <div className={cn("w-full max-w-full", className)} style={style}>
        <EmbedPlayer
          className="w-full max-w-full"
          provider={att.embedProvider}
          videoId={att.embedVideoId}
          embedUrl={att.embedUrl}
          title={att.name ?? "embed"}
          eager
        />
      </div>
    );
  }

  const imageUrlForSize = (s: MediaSize | undefined): string | undefined => {
    const petit = att?.publicUrl;
    const medium = att?.mediumPublicUrl;
    const large = att?.largePublicUrl;
    const original = (att as { originalPublicUrl?: string } | undefined)
      ?.originalPublicUrl;
    switch (s) {
      case "petit":
        return petit ?? medium ?? large;
      case "medium":
        return medium ?? large ?? petit;
      case "original":
        return original ?? large ?? medium ?? petit;
      case "large":
      default:
        return large ?? medium ?? petit;
    }
  };
  const url =
    imageUrlForSize(size ?? "large") ??
    (att
      ? resolveEmbedThumbnailUrl(
          att.embedProvider,
          att.embedVideoId,
          att.embedUrl,
        ) ?? undefined
      : undefined);

  if (!url) {
    return (
      <div
        className={
          className ??
          "flex h-40 items-center justify-center rounded border border-dashed border-slate-300 text-sm text-slate-400"
        }
        style={style}
      >
        {cmsNew("cms.new.preview.media.empty", "Aucun média sélectionné")}
      </div>
    );
  }

  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img src={url} alt={att?.name ?? ""} className={className} style={style} />
  );
}
